import React, { useState, useEffect, useMemo, useCallback } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import InfoIcon from "@material-ui/icons/Info";

import "./style.scss";
import { listNotificationSchedule, updateNotificationSchedule } from "services/NexiDigitalAPI";
import { Button, Form, InputGroup } from "react-bootstrap";
import { BiTrash } from "react-icons/bi";
import {
  calculateMinMaxTeamEnd,
  calculateMinMaxTeamStart,
} from "containers/PeopleCraft/ClassManagement/CreateNewClass/utils";
import { useToast } from "hooks/useToast";

interface ModalNotificationConfigProps {
  type: string;
  description: string;
  idNotification: number;
  idTeam: number;
  finalDate: string;
  initialDate: string;
  duration?: number;
  status?: string;
  originNotificationValues?: any;
  callback?: (values) => void;
}

const ModalNotificationConfig = NiceModal.create((props: ModalNotificationConfigProps) => {
  const [schedules, setSchedule] = useState([]);
  const [errors, setErrors] = useState<string[]>([]);
  const { error } = useToast();
  const isTeamProgress = props.type === "TEAM_PROGRESS";

  const { min, max } = useMemo(() => {
    if (props.type === "TEAM_START") {
      return calculateMinMaxTeamStart({
        initialDate: props.initialDate,
        finalDate: props.finalDate,
        duration: props.duration,
      });
    }
    if (props.type === "TEAM_FINISH") {
      return calculateMinMaxTeamEnd({
        initialDate: props.initialDate,
        finalDate: props.finalDate,
        duration: props.duration,
      });
    }
    return {
      min: 1,
      max: 99,
    };
  }, [props.duration, props.finalDate, props.initialDate, props.type]);

  const validateRuleTeamStart = useCallback(() => {
    if (min === null  ||  min > max) {
      return false;
    }
    return true;
  }, [max, min]);

  const validateRulesOpenModal = useCallback(
    (values) => {

      const newErrors = values.map((schedule) => {
        const existInBackup_TEAM_START = props?.originNotificationValues['TEAM_START']?.find(
          (elem) =>
            elem.days === schedule.days
        );
          const existInBackup_TEAM_FINISH = props?.originNotificationValues['TEAM_FINISH']?.find(
          (elem) =>
            elem.days === schedule.days
        );
        if(props.type === "TEAM_START" && existInBackup_TEAM_START){
          return ""
        }

        if(props.type === "TEAM_FINISH" && existInBackup_TEAM_FINISH){
          return ""
        }
        const { days } = schedule;

        if (days === null || days === undefined) {
          return "";
        }

        if (props.type === "TEAM_START" && !validateRuleTeamStart()) {
          return "Remova o valor do Dias  para continuar.";
        }

        if (!validateMinMax(days, min, max)) {
          return `Remova ou insira um valor dentro do intervalo permitido de ${min} a ${max}.`;
        }

        return ""; // sem erro para esse item
      });
      setErrors(newErrors);
    },
    [max, min, props?.originNotificationValues, props.type, validateRuleTeamStart]
  );

  const getValues = useCallback(async () => {
    const { data } = await listNotificationSchedule(props.idTeam, props.idNotification);
    setSchedule(data);
    validateRulesOpenModal(data);
  }, [props.idTeam, props.idNotification, validateRulesOpenModal]);

  useEffect(() => {
    getValues();
  }, [getValues]);

  const handleAdd = () => {
    if (disabledInput) return;
    setErrors([...errors, ""]);

    setSchedule([...schedules, { days: "", progress: "" }]);
  };

  const validateMinMax = (value: string, min?: number, max?: number): boolean => {
    if (value === "") {
      return true;
    }
    const numericValue = Number(value);
    if (min !== undefined && max !== undefined) {
      if (numericValue < min || numericValue > max) {
        return false;
      }
    }
    return true;
  };

  const addDays = (e: React.ChangeEvent<any>, index) => {
    if (disabledInput) return;
    const value = e.target.value;
    if (!validateMinMax(value, min, max)) {
      e.stopPropagation();
      return;
    }
    const newSchedules = [...schedules];
    const newErrors = [...errors];
    if (props.type === "TEAM_START" && !validateRuleTeamStart()) {
      return;
    }

    if (isNaN(value)) {
      newErrors[index] = "Insira um valor válido.";
      setErrors(newErrors);
      return;
    }

    newErrors[index] = "";
    const _value = value ? Number(value) : value;
    const _newSchedule = isTeamProgress
      ? { days: null, progress: _value }
      : { days: _value, progress: null };

    newSchedules[index] = _newSchedule;

    setSchedule(newSchedules);
    setErrors(newErrors);
  };

  const handleRemove = (index) => {
    if (disabledInput) return;
    const newSchedules = [...schedules];
    const newErrors = [...errors];
    newSchedules.splice(index, 1);
    newErrors.splice(index, 1);
    setSchedule(newSchedules);
    setErrors(newErrors);
  };

  const checkConditions = (schedule) =>
    isTeamProgress
      ? schedule.progress == null || schedule.progress.toString() === "" || isNaN(schedule.progress)
      : schedule.days == null || schedule.days.toString() === "" || isNaN(schedule.days);

  const _handleSave = async () => {
    if (isTeamProgress && schedules.some((schedule) => schedule.progress > 100)) {
      error("O período não pode ultrapassar 100%");
      return;
    }
    if (schedules.some(checkConditions)) {
      error("Insira um valor válido.");
      return;
    }
    try {
      await updateNotificationSchedule(props.idTeam, props.idNotification, schedules);
      props.callback({
        type: props.type,
        values: schedules,
      });
      modal.remove();
    } catch (e) {
      error("Erro ao salvar as notificações.");
      return;
    }
  };

  const modal = useModal();

  const disabledInput = useMemo(() => {
    return (
      props.status === "FINISHED" || !props.initialDate || (!props.duration && !props.finalDate)
    );
  }, [props.duration, props.finalDate, props.initialDate, props.status]);

  const description = useMemo(() => {
    if (props.type === "TEAM_FINISH") {
      return "O número de dias pode ser negativo (notificação será enviada antes da data de fim configurada na turma), zero (notificação enviada na data exata do fim da turma) ou positivo (notificação será enviada após a data de fim configurada na turma).";
    }
    return "O número de dias pode ser negativo (notificação será enviada antes da data de início configurada na turma), zero (notificação enviada na data exata de início da turma) ou positivo (notificação será enviada após a data de início configurada na turma).";
  }, [props.type]);

  return (
    <div className="ModalNotificationConfig">
      <div className={`modal-body-pct`}>
        <div>
          <div className="notification-modal-header">
            <span>Configurar {props.description?.toLowerCase()}</span>
          </div>
          <div className="notification-modal-body">
            {props.type !== "TEAM_PROGRESS" ? (
              <>
                <p className="notification-modal-description">
                  Configure abaixo o envio de {props.description?.toLowerCase()}. É necessário
                  configurar pelo menos uma.
                </p>
                <p className="notification-dialog-info">
                  <InfoIcon
                    style={{
                      color: "#6b48ff",
                      fontSize: 20,
                      marginRight: 10,
                    }}
                  />
                  <small className="notification-dialog-alert">{description}</small>
                </p>
              </>
            ) : (
              <p className="notification-modal-description progress-description">
                Configure abaixo o envio de notificações de andamento de turma para alunos com
                status Não iniciado ou Em andamento. É necessário configurar pelo menos uma.
              </p>
            )}
            <div className="date-container">
              <span className="notification-dialog-date-label">
                {props.type !== "TEAM_PROGRESS"
                  ? "Dias para envio"
                  : "% do período da turma / progresso da matrícula"}
              </span>
              {schedules.map((schedule, index) => (
                <InputGroup className="mb-3 mt-3" key={index}>
                  <Form.Control
                    type="number"
                    min={min}
                    max={max}
                    style={{
                      maxWidth: 100,
                      maxHeight: 35,
                    }}
                    value={isTeamProgress ? schedule.progress : schedule.days}
                    onChange={(e) => addDays(e, index)}
                    isInvalid={!!errors[index]}
                    disabled={disabledInput}
                  />

                  <InputGroup.Append>
                    <div className="date-trash" onClick={() => handleRemove(index)}>
                      <BiTrash color="#dc3545" size={18} />
                    </div>
                  </InputGroup.Append>
                  <Form.Control.Feedback type="invalid">{errors[index]}</Form.Control.Feedback>
                </InputGroup>
              ))}
              <div>
                <span className="add-new-input-date" onClick={handleAdd}>
                  + Adicionar novo
                </span>
              </div>
            </div>
          </div>
          <div className="notification-modal-footer">
            <Button
              variant="secondary"
              className="notification-dialog-cancel-btn"
              onClick={() => modal.remove()}
            >
              Cancelar
            </Button>
            {!disabledInput && (
              <Button
                variant="warning"
                className="notification-dialog-save-btn"
                onClick={_handleSave}
              >
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ModalNotificationConfig;
